// Connaissances métier : réglages techniques par opération, caractéristiques des machines,
// aléas courants par type de poste. Tout est en code pour rester identique en démo et en ligne.
import type { MachineType, Operation, ParamValue, Task } from "./types.ts";

export interface FieldDef {
  key: string;
  label: string;
  kind: "select" | "number" | "bool" | "multi" | "text";
  options?: string[];
  unit?: string;
  hint?: string;
  /** Un changement de cette valeur demande un recalage de la machine. */
  setup?: boolean;
}

// ---------------------------------------------------------------------------
// Machines : opérations possibles par type, et caractéristiques techniques
// ---------------------------------------------------------------------------
export const TYPE_OPS: Record<MachineType, Operation[]> = {
  plieuse: ["pli"],
  encarteuse_piqueuse: ["encartage_piquage", "encartage", "piquage"],
  piqueuse: ["piquage"],
  dos_carre_colle: ["dos_carre_colle"],
  raineur_plieur: ["rainage_pliage", "rainage", "pli"],
  pelliculeuse: ["pelliculage"],
  autre: [],
};

/** Unité de cadence affichée pour chaque type. */
export const SPEED_UNIT: Record<MachineType, string> = {
  plieuse: "feuilles / h", encarteuse_piqueuse: "ex. / h", piqueuse: "ex. / h", dos_carre_colle: "ex. / h",
  raineur_plieur: "feuilles / h", pelliculeuse: "feuilles / h", autre: "ex. / h",
};

export const STAPLES = ["2 points", "4 points", "Boucles (oméga)"];
export const GLUES = ["EVA (hotmelt)", "PUR", "Colle froide"];
export const FILMS = ["Brillant", "Mat", "Soft touch", "Anti-rayure", "Pailleté"];

const COMMON_SPECS: FieldDef[] = [
  { key: "grammage_min", label: "Grammage minimum", kind: "number", unit: "g" },
  { key: "grammage_max", label: "Grammage maximum", kind: "number", unit: "g" },
  { key: "format_max", label: "Format maximum", kind: "text", hint: "Ex. 52 × 74 cm" },
];

export const MACHINE_SPECS: Record<MachineType, FieldDef[]> = {
  plieuse: [
    { key: "poches", label: "Poches de pliage", kind: "number" },
    { key: "couteaux", label: "Couteaux", kind: "number" },
    { key: "colle_ligne", label: "Point de colle en ligne", kind: "bool" },
    ...COMMON_SPECS,
  ],
  encarteuse_piqueuse: [
    { key: "margeurs", label: "Margeurs (postes d'encartage)", kind: "number" },
    { key: "tetes", label: "Têtes d'agrafage", kind: "number" },
    { key: "agrafes", label: "Agrafages possibles", kind: "multi", options: STAPLES, hint: "Aucun coché = tous" },
    { key: "trilaterale", label: "Trilatérale en ligne", kind: "bool" },
    { key: "stacker", label: "Stacker en sortie", kind: "bool" },
    { key: "pages_max", label: "Pages maximum", kind: "number" },
    ...COMMON_SPECS,
  ],
  piqueuse: [
    { key: "tetes", label: "Têtes d'agrafage", kind: "number" },
    { key: "agrafes", label: "Agrafages possibles", kind: "multi", options: STAPLES, hint: "Aucun coché = tous" },
    ...COMMON_SPECS,
  ],
  dos_carre_colle: [
    { key: "stations", label: "Stations (margeurs de cahiers)", kind: "number" },
    { key: "colles", label: "Colles possibles", kind: "multi", options: GLUES, hint: "Aucune cochée = toutes" },
    { key: "dos_max", label: "Épaisseur de dos maximum", kind: "number", unit: "mm" },
    { key: "trilaterale", label: "Trilatérale en ligne", kind: "bool" },
    ...COMMON_SPECS,
  ],
  raineur_plieur: [
    { key: "outils", label: "Outils de rainage", kind: "number" },
    { key: "perforation", label: "Perforation possible", kind: "bool" },
    ...COMMON_SPECS,
  ],
  pelliculeuse: [
    { key: "films", label: "Films possibles", kind: "multi", options: FILMS, hint: "Aucun coché = tous" },
    { key: "laize_max", label: "Laize maximum", kind: "number", unit: "mm" },
    { key: "recto_verso", label: "Recto-verso en un passage", kind: "bool" },
    ...COMMON_SPECS,
  ],
  autre: COMMON_SPECS,
};

// ---------------------------------------------------------------------------
// Étapes de job : réglages techniques par opération
// ---------------------------------------------------------------------------
const STITCH: FieldDef[] = [
  { key: "agrafes", label: "Agrafage", kind: "select", options: STAPLES, setup: true },
  { key: "cahiers", label: "Cahiers à encarter", kind: "number", setup: true },
  { key: "couverture", label: "Couverture", kind: "select", options: ["Même papier", "Couverture séparée"] },
  { key: "trilaterale", label: "Coupe trilatérale", kind: "bool", setup: true },
];

export const OP_FIELDS: Record<Operation, FieldDef[]> = {
  pli: [
    { key: "plis", label: "Nombre de plis", kind: "number", setup: true },
    { key: "colle", label: "Point de colle", kind: "bool", setup: true },
    { key: "perforation", label: "Perforation", kind: "bool", setup: true },
  ],
  encartage_piquage: STITCH,
  encartage: STITCH.filter((f) => f.key !== "agrafes"),
  piquage: STITCH.filter((f) => f.key !== "cahiers"),
  dos_carre_colle: [
    { key: "colle", label: "Colle", kind: "select", options: GLUES, setup: true },
    { key: "dos_mm", label: "Épaisseur du dos", kind: "number", unit: "mm", setup: true },
    { key: "cahiers", label: "Cahiers", kind: "number", setup: true },
    { key: "rainures", label: "Rainures de couverture", kind: "select", options: ["2", "4"], setup: true },
    { key: "trilaterale", label: "Coupe trilatérale", kind: "bool", setup: true },
  ],
  rainage: [
    { key: "rainages", label: "Nombre de rainages", kind: "number", setup: true },
    { key: "perforation", label: "Perforation", kind: "bool", setup: true },
    { key: "sens_fibre", label: "Sens des fibres", kind: "select", options: ["Parallèle au rainage", "Perpendiculaire"] },
  ],
  rainage_pliage: [
    { key: "rainages", label: "Nombre de rainages", kind: "number", setup: true },
    { key: "plis", label: "Nombre de plis", kind: "number", setup: true },
    { key: "perforation", label: "Perforation", kind: "bool", setup: true },
  ],
  pelliculage: [
    { key: "film", label: "Film", kind: "select", options: FILMS, setup: true },
    { key: "faces", label: "Faces", kind: "select", options: ["Recto", "Recto-verso"], setup: true },
    { key: "laize", label: "Laize", kind: "number", unit: "mm", setup: true },
  ],
};

/** L'opération utilise-t-elle un type de pli et un nombre de pages ? */
export const OP_HAS_FOLD = (op: Operation) => op === "pli" || op === "rainage_pliage";
export const OP_HAS_PAGES = (op: Operation) => ["encartage_piquage", "encartage", "piquage", "dos_carre_colle"].includes(op);

/** Valeur lisible d'un réglage. */
export function showValue(f: FieldDef, v: ParamValue): string | null {
  if (v === null || v === undefined || v === "" || (Array.isArray(v) && !v.length)) return null;
  if (f.kind === "bool") return v ? "Oui" : null;
  if (Array.isArray(v)) return v.join(", ");
  return `${v}${f.unit ? ` ${f.unit}` : ""}`;
}

/** Résumé des réglages techniques d'une étape (pour le planning et la tablette). */
export function paramSummary(t: Pick<Task, "operation" | "params">): { label: string; value: string }[] {
  const out: { label: string; value: string }[] = [];
  for (const f of OP_FIELDS[t.operation] || []) {
    const v = showValue(f, t.params?.[f.key] ?? null);
    if (v) out.push({ label: f.label, value: f.kind === "bool" ? f.label : v });
  }
  return out;
}

/** Partie de la clé de réglage qui vient des paramètres techniques. */
export function setupParams(t: Pick<Task, "operation" | "params">): string {
  const parts: string[] = [];
  for (const f of OP_FIELDS[t.operation] || []) {
    if (!f.setup) continue;
    const v = t.params?.[f.key];
    if (v === null || v === undefined || v === "" || v === false) continue;
    parts.push(`${f.key}=${Array.isArray(v) ? v.join("+") : String(v)}`);
  }
  return parts.join(",");
}

/** La machine sait-elle faire ces réglages ? Renvoie la raison sinon. */
export function specConflict(specs: Record<string, ParamValue> | undefined, t: Pick<Task, "operation" | "params" | "grammage" | "pages">): string | null {
  const s = specs || {};
  const num = (k: string) => (typeof s[k] === "number" && (s[k] as number) > 0 ? (s[k] as number) : null);
  const list = (k: string) => (Array.isArray(s[k]) ? (s[k] as string[]) : []);
  const p = t.params || {};
  if (t.grammage && num("grammage_max") && t.grammage > num("grammage_max")!) return `grammage ${t.grammage} g > ${num("grammage_max")} g`;
  if (t.grammage && num("grammage_min") && t.grammage < num("grammage_min")!) return `grammage ${t.grammage} g < ${num("grammage_min")} g`;
  if (t.pages && num("pages_max") && t.pages > num("pages_max")!) return `${t.pages} pages > ${num("pages_max")}`;
  if (typeof p.agrafes === "string" && list("agrafes").length && !list("agrafes").includes(p.agrafes)) return `agrafage ${p.agrafes} non disponible`;
  if (typeof p.colle === "string" && list("colles").length && !list("colles").includes(p.colle)) return `colle ${p.colle} non disponible`;
  if (typeof p.film === "string" && list("films").length && !list("films").includes(p.film)) return `film ${p.film} non disponible`;
  if (typeof p.dos_mm === "number" && num("dos_max") && p.dos_mm > num("dos_max")!) return `dos de ${p.dos_mm} mm > ${num("dos_max")} mm`;
  if (typeof p.laize === "number" && num("laize_max") && p.laize > num("laize_max")!) return `laize ${p.laize} mm > ${num("laize_max")} mm`;
  return null;
}

/** Nombre de passages machine (pelliculage recto-verso sans module recto-verso = 2). */
export function passes(specs: Record<string, ParamValue> | undefined, t: Pick<Task, "operation" | "params">): number {
  if (t.operation === "pelliculage" && t.params?.faces === "Recto-verso" && !(specs || {}).recto_verso) return 2;
  return 1;
}

// ---------------------------------------------------------------------------
// Aléas : liste par défaut de chaque type de poste, avec des causes pour aller vite
// ---------------------------------------------------------------------------
export const DEFAULT_ALEAS: Record<MachineType, string[]> = {
  plieuse: ["Bourrage", "Panne machine", "Attente feuilles", "Réglage poches", "Problème de pli", "Autre"],
  encarteuse_piqueuse: ["Margeurs", "Tête d'agrafage", "Stacker", "Trilatérale", "Bourrage", "Attente cahiers", "Panne machine", "Autre"],
  piqueuse: ["Tête d'agrafage", "Bourrage", "Attente cahiers", "Panne machine", "Autre"],
  dos_carre_colle: ["Colle", "Fraiseuse", "Margeurs", "Couverture", "Trilatérale", "Attente cahiers", "Panne machine", "Autre"],
  raineur_plieur: ["Bourrage", "Rainage", "Problème de pli", "Attente feuilles", "Panne machine", "Autre"],
  pelliculeuse: ["Film", "Température / pression", "Séparation des feuilles", "Margeur", "Attente feuilles", "Panne machine", "Autre"],
  autre: ["Bourrage", "Panne machine", "Attente feuilles", "Autre"],
};

export const ALEA_CAUSES: Record<string, string[]> = {
  "Bourrage": ["Feuilles collées", "Électricité statique", "Feuilles froissées", "Réglage des taquets", "Papier humide"],
  "Panne machine": ["Courroie", "Rouleaux", "Aspiration", "Électrique", "Capteur"],
  "Attente feuilles": ["Impression pas terminée", "Séchage", "Massicot", "Manutention"],
  "Attente cahiers": ["Pliage pas terminé", "Impression pas terminée", "Manutention"],
  "Réglage poches": ["Butée", "Rouleaux de pli", "Format"],
  "Problème de pli": ["Pli décalé", "Pli qui casse", "Marquage", "Sens des fibres"],
  "Margeurs": ["Double feuille", "Cahier mal ouvert", "Aspiration", "Pile mal taquée"],
  "Tête d'agrafage": ["Fil d'agrafe terminé", "Agrafe mal formée", "Bourrage de fil", "Réglage hauteur"],
  "Stacker": ["Pile mal formée", "Bourrage en sortie", "Compteur"],
  "Trilatérale": ["Lame", "Coupe décalée", "Bourrage"],
  "Colle": ["Température", "Niveau de colle", "Collage insuffisant", "Fils de colle"],
  "Fraiseuse": ["Lame usée", "Profondeur", "Poussière"],
  "Couverture": ["Rainage couverture", "Couverture décalée", "Double"],
  "Rainage": ["Rainage qui éclate", "Outil", "Profondeur"],
  "Film": ["Fin de bobine", "Plis dans le film", "Bulles", "Adhérence"],
  "Température / pression": ["Rouleau froid", "Pression trop forte", "Ondulation"],
  "Séparation des feuilles": ["Couteau", "Perforateur", "Feuilles collées"],
  "Margeur": ["Double feuille", "Aspiration", "Pile mal taquée"],
  "Autre": ["Contrôle qualité", "Changement de palette", "Nettoyage", "Réunion"],
};

/** Aléas proposés sur un poste : liste du type (personnalisée ou par défaut) + ajouts du poste. */
export function aleasFor(type: MachineType, typeList: string[], machineList: string[]): string[] {
  const base = typeList.length ? typeList : DEFAULT_ALEAS[type] || DEFAULT_ALEAS.autre;
  return [...base, ...machineList.filter((x) => !base.includes(x))];
}

/** Suggestions pour démarrer la liste des conditionnements. */
export const PACKAGING_SUGGESTIONS: Partial<Record<MachineType, string[]>> = {
  plieuse: ["En vrac en carton", "Paquets de 100 sous film", "Paquets de 50 sous film", "Banderolé par 100", "Sur palette filmée"],
  encarteuse_piqueuse: ["Colisage par 50", "Colisage par 100", "Banderolé par 25", "Mise sous film individuelle", "Sur palette filmée"],
  dos_carre_colle: ["Colisage par 20", "Colisage par 50", "Mise sous film individuelle", "Sur palette filmée"],
  pelliculeuse: ["Pile sur palette", "Intercalaires tous les 500", "Retour en impression"],
  raineur_plieur: ["En vrac en carton", "Paquets de 100 sous film", "Banderolé par 50"],
};

/** Types de machines capables d'une opération. */
export function typesFor(op: Operation): MachineType[] {
  return (Object.keys(TYPE_OPS) as MachineType[]).filter((t) => TYPE_OPS[t].includes(op));
}
